import { FormActions } from "../../components/common/FormActions";
import {
  createColumn,
  createEditAction,
  createViewAction,
  formatCurrency,
  formatDateTime,
  getForm,
  getRegister,
  hasWorkflow,
} from "../../utils/helpers";
import { URL_DEBIT_HOLDINGS } from "../../utils/urls";
import { IFormSection } from "../../types/common";
import { BooleanTags } from "../../components/common/registerUtils";
import useSet from "../common/useSet";
import { API_DEBIT_HOLDINGS, API_DEBIT_HOLDINGS_TYPES } from "../../utils/apis";
import { useEffect } from "react";
import { message } from "antd";
import { DEBIT_HOLDINGS } from "../../utils/constants";
import { useAuth } from "../../context/AuthContext";
import useGet from "../common/useGet";

const api = API_DEBIT_HOLDINGS;
const url = URL_DEBIT_HOLDINGS;

export const useDebitHolding = () => {
  const { user } = useAuth();
  const { uploadData, error, success } = useSet();

  const tableColumns = () => [
    createColumn("account_no", true, "Account No"),
    createColumn("amount", true, "Amount"),
    // createColumn("initiated_by", true, "Initiated By"),
    createColumn("status", true, "Status"),
    createColumn("bank_code", true, "Bank Code"),
    // createColumn("is_approved", true, "Is Approved"),
    // createColumn("is_rejected", true, "Is Rejected"),
    // createColumn("approved_at", true, "Approved At"),
    // createColumn("rejected_at", true, "Rejected At"),
    createColumn("created_at", true, "Created At"),
    createColumn("actions", false, "Actions"),
  ];

  const handleApproveDebitHolding = (id: string) => {
    uploadData({
      api: `${api}${id}/approve-reject/`,
      method: "POST",
      params: {
        is_approved: true,
      },
    });
  };

  const handleRejectDebitHolding = (id: string, reason?: string) => {
    uploadData({
      api: `${api}${id}/approve-reject/`,
      method: "POST",
      params: {
        is_rejected: true,
        rejected_reason: reason,
      },
    });
  };

  const handleVerifyDebitHolding = (id: string) => {
    uploadData({
      api: `${api}${id}/approve-reject/`,
      method: "POST",
      params: {
        is_verified: true,
      },
    });
  };

  useEffect(() => {
    if (success) {
      message.success(`Updated debit holding status`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      message.error(error?.detail || "Failed to update debit holding status");
    }
  }, [success, error]);

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        created_at: formatDateTime(item?.created_at),
        approved_at: formatDateTime(item?.approved_at),
        amount: formatCurrency(item?.amount),
        rejected_at: formatDateTime(item?.rejected_at),
        is_approved: <BooleanTags status={item?.is_approved} />,
        is_rejected: <BooleanTags status={item?.is_rejected} />,
        actions: (
          <FormActions
            actions={[
              ...(hasWorkflow(user?.workflows, DEBIT_HOLDINGS, 1)
                ? [createEditAction(url, item?.id)]
                : []),
              createViewAction(url, item?.id),
              ...(!item?.is_verified &&
              item?.status === "pending" &&
              hasWorkflow(user?.workflows, DEBIT_HOLDINGS, 2)
                ? [
                    {
                      label: "Reject",
                      custom: true,
                      showReasonField: true,
                      deleteMessage:
                        "Please provide a reason for rejecting this debit holding.",
                      handleCustom: (reason?: string) =>
                        handleRejectDebitHolding(item.id, reason),
                    },
                    {
                      label: "Verify",
                      custom: true,
                      showReasonField: false,
                      handleCustom: () => handleVerifyDebitHolding(item.id),
                    },
                  ]
                : []),
              ...(!item?.is_approved &&
              item?.status === "verified" &&
              hasWorkflow(user?.workflows, DEBIT_HOLDINGS, 3)
                ? [
                    {
                      label: "Reject",
                      custom: true,
                      showReasonField: true,
                      deleteMessage:
                        "Please provide a reason for rejecting this debit holding.",
                      handleCustom: (reason?: string) =>
                        handleRejectDebitHolding(item.id, reason),
                    },
                    {
                      label: "Approve",
                      custom: true,
                      showReasonField: false,
                      handleCustom: () => handleApproveDebitHolding(item.id),
                    },
                  ]
                : []),
            ]}
          />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Debit Holdings Details",
          fields: {
            account_no: item?.account_no,
            amount: item?.amount,
            initiated_by: item?.initiated_by,
            verified_by: item?.verified_by,
            verified_at: item?.verified_at,
            status: item?.status,
            disbursement_ref: item?.disbursement_ref,
            bank_code: item?.bank_code,
            approved_by: item?.approved_by,
            is_approved: item?.is_approved,
            is_rejected: item?.is_rejected,
            approved_at: item?.approved_at,
            rejected_at: item?.rejected_at,
            rejected_reason: item?.rejected_reason,
            currency: item?.currency,
            created_at: item?.created_at,
            updated_at: item?.updated_at,
            created_by: item?.created_by,
          },
        },
      ],
    };
  };

  const formRoute = hasWorkflow(user?.workflows, DEBIT_HOLDINGS, 1)
    ? getForm(url)
    : null;
  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
  };
};

export const useDebitHoldingForm = () => {
  const {
    data: debitHoldingTypes,
    loadData: loadDebitHoldingTypes,
    loading,
    error,
  } = useGet();

  useEffect(() => {
    loadDebitHoldingTypes({ api: API_DEBIT_HOLDINGS_TYPES });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "Debit Holdings",
      fields: [
        {
          field_name: "account_no",
          label: "Account No",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "amount",
          label: "Amount",
          type: "decimal",
          props: { required: true, min: 0 },
        },
        {
          field_name: "currency",
          label: "Currency",
          type: "text",
          props: { required: true, defaultValue: "UGX", disabled: true },
        },
        {
          field_name: "bank_code",
          label: "Bank Code",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "disbursement_ref",
          label: "Disbursement Ref",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "debit_holding_type",
          label: "Debit Holding Type",
          type: "select",
          props: { required: true },
          options_data: debitHoldingTypes?.results?.map((type: any) => {
            return {
              id: type.type_code,
              name: type.debit_holding_type,
            };
          }),
          options_name: "debit_holding_types",
        },
      ],
    },
  ];

  const formRoute = getRegister(url);
  const redirect = getRegister(url);

  return {
    sections,
    formRoute,
    redirect,
    error,
    loading,
    defaultValues: {
      currency: "UGX",
    },
  };
};
